// Value formatting by the `format` token that comes from metric/column config. The frontend
// never decides what a number means — only how a declared format is displayed.

const nf = new Map<string, Intl.NumberFormat>();
function num(opts: Intl.NumberFormatOptions) {
  const k = JSON.stringify(opts);
  let f = nf.get(k);
  if (!f) nf.set(k, (f = new Intl.NumberFormat('en-GB', opts)));
  return f;
}

export interface FormatOpts {
  currency?: string | null;
  compact?: boolean;
}

export function formatValue(v: unknown, format: string | undefined, opts: FormatOpts = {}): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number' && !Number.isFinite(v)) return '—';
  switch (format) {
    case 'currency': {
      const n = Number(v);
      if (!opts.currency) return num({ maximumFractionDigits: 2 }).format(n);
      if (opts.compact && Math.abs(n) >= 10_000) return num({ style: 'currency', currency: opts.currency, notation: 'compact', maximumFractionDigits: 2 }).format(n);
      if (opts.compact && Number.isInteger(n)) return num({ style: 'currency', currency: opts.currency, maximumFractionDigits: 0 }).format(n);
      return num({ style: 'currency', currency: opts.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    }
    case 'integer': {
      const n = Number(v);
      if (opts.compact && Math.abs(n) >= 100_000) return num({ notation: 'compact', maximumFractionDigits: 1 }).format(n);
      return num({ maximumFractionDigits: 0 }).format(n);
    }
    case 'percent':
      return `${(Number(v) * 100).toFixed(opts.compact ? 1 : 2)}%`;
    case 'signed_percent':
      return formatSignedPct(Number(v));
    case 'ratio':
      return `${Number(v).toFixed(2)}×`;
    case 'days':
      return `${Number(v).toFixed(1)} d`;
    case 'decimal':
      return num({ maximumFractionDigits: 2 }).format(Number(v));
    case 'rate':
      return Number(v).toFixed(4);
    case 'boolean':
      return v ? 'Yes' : 'No';
    case 'date':
      return formatDate(String(v));
    case 'datetime':
      return formatDateTime(String(v));
    case 'relative':
      return relativeTime(String(v));
    case 'duration':
      return formatDuration(Number(v));
    case 'number':
      return num({ maximumFractionDigits: 2 }).format(Number(v));
    default:
      return Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function formatShortDate(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

// Timestamps show in the workspace's reporting time zone (from the shell, UK time by default), with
// its abbreviation (BST/GMT), so they line up with how days are counted. UTC until the shell loads.
let displayTimeZone = 'UTC';
const dtf = new Map<string, Intl.DateTimeFormat>();

export function setDisplayTimeZone(tz: string | null | undefined) {
  if (!tz || tz === displayTimeZone) return;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    displayTimeZone = tz;
  } catch {
    // Unknown zone: keep the previous one.
  }
}

export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

/** Parts of an instant in the display time zone: { day, month, year, weekday, hour, minute, second, zone }. */
export function zonedParts(d: Date, tz = displayTimeZone): Record<string, string> {
  let f = dtf.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', timeZoneName: 'short' });
    dtf.set(tz, f);
  }
  return Object.fromEntries(f.formatToParts(d).map((p) => [p.type, p.value]));
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = zonedParts(d);
  return `${p.day} ${p.month}, ${p.hour}:${p.minute} ${p.timeZoneName}`;
}

/** "09:12:45 BST" today, "15 Oct, 09:12 BST" on another day (display time zone). */
export function formatClockTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = zonedParts(d);
  const today = zonedParts(new Date(now));
  return p.day === today.day && p.month === today.month && p.year === today.year ? `${p.hour}:${p.minute}:${p.second} ${p.timeZoneName}` : formatDateTime(iso);
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return String(iso);
  const s = Math.round((t - now) / 1000);
  const abs = Math.abs(s);
  const fut = s > 0;
  const fmt = (n: number, u: string) => (fut ? `in ${n} ${u}` : `${n} ${u} ago`);
  if (abs < 5) return fut ? 'in a moment' : 'just now';
  if (abs < 60) return fmt(abs, 's');
  if (abs < 3600) return fmt(Math.round(abs / 60), 'min');
  if (abs < 86_400) return fmt(Math.round(abs / 3600), 'h');
  return fmt(Math.round(abs / 86_400), 'd');
}

/** "5 min", "Hourly", "Daily": how often periodically refreshed data updates. */
export function cadenceLabel(everySeconds: number | null | undefined): string {
  const s = Number(everySeconds ?? 0);
  if (!s) return 'Periodic';
  if (s >= 86_400) return s === 86_400 ? 'Daily' : `Every ${Math.round(s / 86_400)} d`;
  if (s >= 3600) return s === 3600 ? 'Hourly' : `Every ${Math.round(s / 3600)} h`;
  return `${Math.round(s / 60)} min`;
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} m ${Math.round((ms % 60_000) / 1000)} s`;
}

export function formatSignedPct(x: number): string {
  const v = Math.abs(x * 100);
  return `${x > 0 ? '+' : x < 0 ? '−' : '±'}${v >= 100 ? v.toFixed(0) : v.toFixed(1)}%`;
}

/** Nice axis ticks (0 / 1,000 / 2,000 …). */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0];
  if (min === max) max = min + 1;
  const span = max - min;
  const step0 = span / count;
  const mag = 10 ** Math.floor(Math.log10(step0));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= step0) ?? 10 * mag;
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step; // last tick always covers the data
  const ticks: number[] = [];
  for (let k = 0; start + k * step <= end + step * 1e-9; k++) ticks.push(Number((start + k * step).toPrecision(12)));
  return ticks;
}

export const ENTITY_SLOT: Record<string, number> = { 'Online Store': 1, 'Point of Sale': 2, Shop: 3, 'Draft orders': 4 };

/** Color follows the entity, never its rank: known entities have fixed slots, others hash to one. */
export function seriesColor(id: string, index: number): string {
  let slot = ENTITY_SLOT[id];
  if (!slot) {
    let h = 0;
    for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    slot = id ? (h % 8) + 1 : (index % 8) + 1;
  }
  return `var(--series-${slot})`;
}
